// Passes (spec/15 P-14): the passes held, with the time left on each, where it can be used, when it expires and
// where it stands with the live session (in use now, up next); then the ones used up or expired. Passes are bought at
// the counter: the empty state leads to the venues and their prices.

import type { MyPass } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, EmptyState, ProgressBar, Skeleton } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { usePasses } from '../api/queries';
import { formatterFor, shortDuration } from '../lib/format';
import { LoadFailure, LoadingRegion, SectionHead } from '../shell/feedback';
import { NavIcon } from '../shell/nav-icons';
import { Page } from '../shell/Page';
import { MoneyTabs } from './WalletPage';

export function PassesPage(): ReactNode {
  const { t } = useTranslation('player');
  const passes = usePasses();

  return (
    <Page
      title={t('passes.title')}
      description={t('passes.subtitle')}
      actions={
        // A walk-in's ticket from the counter, claimed into this account (F-SES-07).
        <Link to="/guest-pass" className="pw-btn pw-btn--secondary">
          {t('passes.claimGuest')}
        </Link>
      }
    >
      <MoneyTabs current="passes" />
      {passes.isPending ? (
        <LoadingRegion className="pw-pass-grid">
          <Skeleton height={176} />
          <Skeleton height={176} />
        </LoadingRegion>
      ) : passes.isError ? (
        <LoadFailure error={passes.error} onRetry={() => void passes.refetch()} />
      ) : passes.data.active.length === 0 && passes.data.past.length === 0 ? (
        <div className="pw-panel">
          <EmptyState
            icon={<NavIcon name="passes" size={32} />}
            title={t('passes.none')}
            description={t('passes.noneBody')}
            action={
              <Link to="/venues" className="pw-btn pw-btn--primary">
                {t('passes.findVenue')}
              </Link>
            }
          />
        </div>
      ) : (
        <>
          <section aria-labelledby="pw-passes-active" className="pw-stack">
            <SectionHead id="pw-passes-active" title={t('passes.active')} />
            {passes.data.active.length === 0 ? (
              <EmptyState
                size="compact"
                title={t('passes.none')}
                description={t('passes.noneBody')}
                action={
                  <Link to="/venues" className="pw-btn pw-btn--secondary">
                    {t('passes.findVenue')}
                  </Link>
                }
              />
            ) : (
              <ul className="pw-pass-grid">
                {passes.data.active.map((pass) => (
                  <PassCard key={pass.id} pass={pass} />
                ))}
              </ul>
            )}
          </section>
          {passes.data.past.length === 0 ? null : (
            <section aria-labelledby="pw-passes-past" className="pw-panel">
              <SectionHead id="pw-passes-past" title={t('passes.past')} />
              <ul className="pw-rows">
                {passes.data.past.map((pass) => (
                  <li key={pass.id} className="pw-row">
                    <span className="pw-row__icon" aria-hidden="true">
                      <NavIcon name="passes" size={20} />
                    </span>
                    <span className="pw-row__main">
                      <span className="pw-row__title">{pass.name}</span>
                      <span className="pw-row__meta">
                        {pass.organization.name} · {formatterFor(pass.price.currency).date(pass.purchasedAt, 'medium')}
                      </span>
                    </span>
                    <Badge tone="neutral">{t(`passes.status.${pass.remainingSeconds === 0 && pass.status === 'active' ? 'exhausted' : pass.status}`)}</Badge>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </Page>
  );
}

function PassCard({ pass }: { readonly pass: MyPass }): ReactNode {
  const { t } = useTranslation('player');
  const fmt = formatterFor(pass.price.currency);
  const left = t('passes.left', { duration: shortDuration(pass.remainingSeconds) });
  return (
    <li className="pw-passcard" data-place={pass.place}>
      <div className="pw-passcard__top">
        <h3 className="pw-passcard__name">{pass.name}</h3>
        {pass.place === 'held' ? null : <Badge tone="session" dot>{t(`passes.place.${pass.place}`)}</Badge>}
      </div>
      <p className="pw-passcard__org">{pass.organization.name}</p>
      <p className="pw-passcard__left">
        <span className="clav-num">{shortDuration(pass.remainingSeconds)}</span>
        <span className="pw-subtle"> {t('passes.of', { duration: shortDuration(pass.grantedSeconds) })}</span>
      </p>
      <ProgressBar value={pass.grantedSeconds === 0 ? 0 : pass.remainingSeconds / pass.grantedSeconds} label={left} />
      <ul className="pw-passcard__facts">
        <li>
          <NavIcon name="clock" size={16} />
          {pass.expiresAt === null ? t('passes.expiresAfterFirstUse') : t('passes.expires', { date: fmt.date(pass.expiresAt, 'medium') })}
        </li>
        <li>
          <NavIcon name="venues" size={16} />
          {pass.usableAt === null
            ? t('passes.usableEverywhere', { organization: pass.organization.name })
            : t('passes.usableAt', { venues: pass.usableAt.join(', ') })}
        </li>
        <li>
          <NavIcon name="orders" size={16} />
          {pass.price.amount === 0 ? t('passes.free') : t('passes.paid', { amount: fmt.money(pass.price) })}
        </li>
      </ul>
    </li>
  );
}
